import { useCallback, useMemo } from 'react';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsString,
  useQueryState,
} from 'nuqs';

import { isAllGrades } from '@/views/build/util';

export const SEARCH_FILTER_QUERY_KEYS = [
  'sfSubject',
  'sfPublishers',
  'sfOfficial',
  'sfTemplateType',
  'sfQuizCountRange',
  'sfGrades',
  'sfSemesters',
] as const;

const queryOptions = {
  history: 'replace' as const,
  shallow: true,
};

export const getNextSearchFilterGrades = (
  currentGrades: readonly string[],
  grade: string,
) => {
  return currentGrades.includes(grade)
    ? currentGrades.filter(value => value !== grade)
    : [...currentGrades, grade].sort();
};

export const shouldResetSearchFilterSemesters = (
  nextGrades: readonly string[],
) =>
  nextGrades.length === 0 ||
  (nextGrades.length === 1 && isAllGrades(nextGrades[0]));

export const getNextSubjectAfterGradeChange = (currentSubject: string) =>
  currentSubject;

export const usePublicSearchFilterQuery = () => {
  const [subject, setSubject] = useQueryState('sfSubject', parseAsString);
  const [publishers, setPublishers] = useQueryState(
    'sfPublishers',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [official, setOfficial] = useQueryState('sfOfficial', parseAsBoolean);
  const [templateType, setTemplateType] = useQueryState(
    'sfTemplateType',
    parseAsString,
  );
  const [quizCountRange, setQuizCountRange] = useQueryState(
    'sfQuizCountRange',
    parseAsString,
  );
  const [grades, setGradeQuery] = useQueryState(
    'sfGrades',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [semesters, setSemesters] = useQueryState(
    'sfSemesters',
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  const hasActiveSearchFilters = Boolean(
    subject ||
      publishers.length > 0 ||
      official ||
      templateType ||
      quizCountRange ||
      grades.length > 0 ||
      semesters.length > 0,
  );

  const state = useMemo(
    () => ({
      subject: subject ?? '',
      publishers,
      official: official ?? false,
      templateType: templateType ?? '',
      quizCountRange: quizCountRange ?? '',
      grades,
      semesters,
    }),
    [
      grades,
      official,
      publishers,
      quizCountRange,
      semesters,
      subject,
      templateType,
    ],
  );

  const clearSearchFilters = useCallback(() => {
    void setSubject(null, queryOptions);
    void setPublishers(null, queryOptions);
    void setOfficial(null, queryOptions);
    void setTemplateType(null, queryOptions);
    void setQuizCountRange(null, queryOptions);
    void setGradeQuery(null, queryOptions);
    void setSemesters(null, queryOptions);
  }, [
    setGradeQuery,
    setOfficial,
    setPublishers,
    setQuizCountRange,
    setSemesters,
    setSubject,
    setTemplateType,
  ]);

  const togglePublisher = (publisher: string) => {
    const next = publishers.includes(publisher)
      ? publishers.filter(value => value !== publisher)
      : [...publishers, publisher];
    void setPublishers(next.length > 0 ? next : null, queryOptions);
  };

  const setSearchFilterGrades = (nextGrades: string[]) => {
    void setGradeQuery(nextGrades.length > 0 ? nextGrades : null, queryOptions);
    void setSubject(
      getNextSubjectAfterGradeChange(subject ?? '') || null,
      queryOptions,
    );
    void setPublishers(null, queryOptions);

    if (shouldResetSearchFilterSemesters(nextGrades)) {
      void setSemesters(null, queryOptions);
    }
  };

  const toggleGrade = (grade: string) => {
    setSearchFilterGrades(getNextSearchFilterGrades(grades, grade));
  };

  const toggleSemester = (semester: string) => {
    const next = semesters.includes(semester)
      ? semesters.filter(value => value !== semester)
      : [...semesters, semester].sort();
    void setSemesters(next.length > 0 ? next : null, queryOptions);
  };

  return {
    state,
    hasActiveSearchFilters,
    setSubject: (value: string) => setSubject(value || null, queryOptions),
    setOfficial: (value: boolean) =>
      setOfficial(value ? true : null, queryOptions),
    setTemplateType: (value: string) =>
      setTemplateType(value || null, queryOptions),
    setQuizCountRange: (value: string) =>
      setQuizCountRange(value || null, queryOptions),
    setPublishers: (value: string[]) =>
      setPublishers(value.length > 0 ? value : null, queryOptions),
    setGrades: setSearchFilterGrades,
    setSemesters: (value: string[]) =>
      setSemesters(value.length > 0 ? value : null, queryOptions),
    togglePublisher,
    toggleGrade,
    toggleSemester,
    clearSearchFilters,
  };
};
